import { useEffect, useRef, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Loader2, X, MapPin, RefreshCw } from "lucide-react";

// Draws the HOMIIE watermark + capture metadata onto a canvas from a source (video or image)
function drawWatermark(canvas, source, { w, h }, meta) {
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(source, 0, 0, w, h);

    // bottom info panel
    const barH = Math.max(96, Math.round(h * 0.2));
    const grad = ctx.createLinearGradient(0, h - barH, 0, h);
    grad.addColorStop(0, "rgba(16,42,67,0)");
    grad.addColorStop(0.35, "rgba(16,42,67,0.55)");
    grad.addColorStop(1, "rgba(16,42,67,0.85)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, h - barH, w, barH);

    const pad = Math.round(w * 0.03);
    const base = Math.max(18, Math.round(w * 0.028));
    ctx.textBaseline = "top";

    // HOMIIE brand + kind chip (top-left)
    ctx.font = `700 ${Math.round(base * 1.2)}px Inter, Arial, sans-serif`;
    ctx.fillStyle = "rgba(255,255,255,0.95)";
    ctx.fillText("HOMIIE", pad, pad);
    const chip = meta.kind === "before" ? "BEFORE WORK" : "AFTER WORK";
    ctx.font = `800 ${base}px Inter, Arial, sans-serif`;
    const chipW = ctx.measureText(chip).width + pad;
    ctx.fillStyle = meta.kind === "before" ? "rgba(242,140,40,0.92)" : "rgba(16,185,129,0.92)";
    ctx.fillRect(pad, pad + Math.round(base * 1.5), chipW, Math.round(base * 1.5));
    ctx.fillStyle = "#fff";
    ctx.fillText(chip, pad + pad / 2, pad + Math.round(base * 1.65));

    // bottom lines
    const lines = [
        `${meta.date}  ·  ${meta.time}`,
        `${meta.city || "Jodhpur"}  ·  ${meta.bookingRef}`,
        meta.gps ? `GPS ${meta.gps.lat.toFixed(5)}, ${meta.gps.lng.toFixed(5)} (±${Math.round(meta.gps.accuracy)}m)` : "GPS unavailable",
    ];
    ctx.font = `600 ${base}px Inter, Arial, sans-serif`;
    let y = h - barH + Math.round(barH * 0.18);
    for (const ln of lines) {
        ctx.fillStyle = "rgba(255,255,255,0.95)";
        ctx.fillText(ln, pad, y);
        y += Math.round(base * 1.35);
    }
}

export function EvidenceCapture({ bookingId, bookingRef, city, kind, onCaptured, onClose }) {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const fileRef = useRef(null);
    const streamRef = useRef(null);
    const [gps, setGps] = useState(null);
    const [gpsState, setGpsState] = useState("locating"); // locating | ok | denied
    const [camReady, setCamReady] = useState(false);
    const [camError, setCamError] = useState(false);
    const [busy, setBusy] = useState(false);

    const stopCam = useCallback(() => {
        if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    }, []);

    const startCam = useCallback(async () => {
        setCamError(false); setCamReady(false);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
            streamRef.current = stream;
            if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); setCamReady(true); }
        } catch { setCamError(true); }
    }, []);

    useEffect(() => {
        startCam();
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => { setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }); setGpsState("ok"); },
                () => setGpsState("denied"),
                { enableHighAccuracy: true, timeout: 8000 }
            );
        } else setGpsState("denied");
        return () => stopCam();
    }, [startCam, stopCam]);

    const meta = () => {
        const now = new Date();
        return { kind, city, bookingRef, gps, date: now.toLocaleDateString("en-IN"), time: now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) };
    };

    const uploadCanvas = async () => {
        const canvas = canvasRef.current;
        const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.88));
        const fd = new FormData();
        fd.append("file", blob, `evidence_${kind}_${Date.now()}.jpg`);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post(`/bookings/${bookingId}/evidence`, {
            kind, path: data.path, file_id: data.id,
            lat: gps?.lat ?? null, lng: gps?.lng ?? null, accuracy: gps?.accuracy ?? null,
            captured_at: new Date().toISOString(),
        });
    };

    const captureFromCamera = async () => {
        if (!videoRef.current) return;
        setBusy(true);
        try {
            const v = videoRef.current;
            drawWatermark(canvasRef.current, v, { w: v.videoWidth || 1280, h: v.videoHeight || 720 }, meta());
            await uploadCanvas();
            toast.success(`${kind === "before" ? "Before" : "After"} photo captured`);
            onCaptured?.();
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const captureFromFile = async (e) => {
        const file = e.target.files?.[0]; if (!file) return;
        if (Date.now() - (file.lastModified || 0) > 3 * 60 * 1000) { e.target.value = ""; return toast.error("Please take a fresh photo with the camera — gallery photos aren't accepted as evidence."); }
        setBusy(true);
        try {
            const img = new Image();
            const url = URL.createObjectURL(file);
            await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
            drawWatermark(canvasRef.current, img, { w: img.naturalWidth, h: img.naturalHeight }, meta());
            URL.revokeObjectURL(url);
            await uploadCanvas();
            toast.success(`${kind === "before" ? "Before" : "After"} photo added`);
            onCaptured?.();
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); e.target.value = ""; }
    };

    const close = () => { stopCam(); onClose?.(); };

    return (
        <div className="fixed inset-0 z-[60] flex flex-col bg-black" data-testid="evidence-capture">
            <div className="flex items-center justify-between px-5 pt-5 text-white">
                <div>
                    <p className="text-xs text-white/50">Capture {kind} photo</p>
                    <h2 className="font-heading text-lg font-bold">{bookingRef}</h2>
                </div>
                <button data-testid="evidence-close-btn" onClick={close} className="rounded-full bg-white/10 p-2"><X size={20} /></button>
            </div>

            <div className="relative mx-auto mt-4 flex w-full max-w-md flex-1 items-center justify-center overflow-hidden px-5">
                <div className="relative w-full overflow-hidden rounded-2xl bg-navy/60" style={{ aspectRatio: "3 / 4" }}>
                    {!camError ? (
                        <video ref={videoRef} playsInline muted className="h-full w-full object-cover" />
                    ) : (
                        <div className="flex h-full flex-col items-center justify-center p-6 text-center text-white/70" data-testid="evidence-cam-blocked">
                            <Camera size={34} className="mb-2 opacity-60" />
                            <p className="text-sm font-semibold text-white">Camera access is needed</p>
                            <p className="mt-1 text-xs">If you tapped “Block” by mistake: open your browser’s site settings (lock icon next to the address) → Camera → Allow, then tap Retry camera. Or use Open camera below.</p>
                        </div>
                    )}
                    {!camReady && !camError && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-white/70" /></div>}
                    <div className="absolute left-3 top-3 rounded-full bg-black/40 px-2.5 py-1 text-[11px] font-bold text-white backdrop-blur">HOMIIE · {kind === "before" ? "BEFORE" : "AFTER"}</div>
                </div>
            </div>

            <div className="mx-auto w-full max-w-md px-5 pb-7 pt-4">
                <div className="mb-3 flex items-center justify-center gap-1.5 text-xs font-medium text-white/70" data-testid="evidence-gps-state">
                    <MapPin size={13} className={gpsState === "ok" ? "text-emerald-400" : "text-amber-400"} />
                    {gpsState === "locating" ? "Getting your location…" : gpsState === "ok" ? `Location captured (±${Math.round(gps.accuracy)}m)` : "Location unavailable — enable GPS for verified evidence"}
                </div>
                <div className="flex items-center gap-3">
                    {!camError && (
                        <button data-testid="evidence-capture-btn" disabled={busy || !camReady} onClick={captureFromCamera} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-orange py-3.5 text-sm font-bold text-white active:scale-95 disabled:opacity-60">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera size={18} />} CAPTURE
                        </button>
                    )}
                    {camError && (
                        <button data-testid="evidence-retry-cam-btn" onClick={startCam} className="flex items-center justify-center gap-2 rounded-full border border-white/25 px-4 py-3.5 text-sm font-bold text-white"><RefreshCw size={16} /> Retry camera</button>
                    )}
                    {camError && (
                        <button data-testid="evidence-native-cam-btn" disabled={busy} onClick={() => fileRef.current?.click()} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-orange py-3.5 text-sm font-bold text-white active:scale-95 disabled:opacity-60">
                            <Camera size={18} /> Open camera
                        </button>
                    )}
                    <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={captureFromFile} />
                </div>
                <button data-testid="evidence-done-btn" onClick={close} className="mt-3 w-full rounded-full py-2.5 text-sm font-bold text-white/70">DONE</button>
            </div>
            <canvas ref={canvasRef} className="hidden" />
        </div>
    );
}
